import { Observable } from "rxjs";
import { OrderStatuses } from "src/app/pages/merchant/food-order/models";
import { OrderItem } from "src/app/pages/merchant/food-order/models/food-order.detail";
import { PaginationDto } from "./dto.interface";
import { GeoLocationAddress } from "../google-map.service";
import { Restaurant } from "../restaurant-selection.service";

export interface RestaurantAddress extends GeoLocationAddress {
  id: string;
  name: string;
}

export interface OrderConfigSetting {
  deliveryPriceIncreasePercentage: number;
}
export interface RestaurantProfile {
  name: string;
  address: RestaurantAddress;
  addresses: RestaurantAddress[];
  phoneNumber: string;
  email: string;
  website?: string;
  logoUrl?: string;
  bgUrl?: string;
  menuIds: string[];
  cuisines: string[];
  timezone: string;
  activeLocationId: string;
  orderConfigSetting: OrderConfigSetting;
  isActive: boolean;
}

export enum MenuStatuses {
  ACTIVE = "active",
  INACTIVE = "inactive",
  ARCHIVED = "archived",
}

export interface DayHours {
  startHour: string | null; // "HH:MM:SS" format or null
  endHour: string | null; // "HH:MM:SS" format or null
}

export interface Constants {
  orderStatuses: {
    id: OrderStatuses;
    name: string;
  }[];
  timezones: {
    id: string;
    name: string;
  }[];
}

export type MenuProfile = {
  id: string;
  name: string;
  status: MenuStatuses;
  hours: { [key: number]: DayHours | null }; // 0-6, where 0 is Sunday
  categoryId?: string[];
  categories?: Category[];
};

export type MenuShallowProfile = {
  id: string;
  name: string;
  hours: { [key: number]: DayHours | null }; // 0-6, where 0 is Sunday
  status: MenuStatuses;
  categories?: Category[];
};

export type CreateMenuParam = Omit<MenuProfile, "id">;

export interface Category {
  id: string;
  name: string;
  menuIds: string[];
  items: FoodItem[];
  order: number;
  displayType: MenuCategoryDisplayType;
}

export interface Menu {
  id: string;
  restaurantId: string;
  name: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface CategoryWithMenu {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  menus?: Menu[];
  items?: FoodItemDTO[];
}

export enum MenuCategoryDisplayType {
  VERTICAL = "VERTICAL",
  HORIZONTAL = "HORIZONTAL",
}

export interface CreateCategoryParam {
  name: string;
  menuIds: string[];
}

export interface UpdateRestauranProfileParam {
  name: string;
  address: Omit<RestaurantAddress, "id">;
  phoneNumber: string;
  // email: string;
  timezone: string;
  cuisines: string[];
  website?: string;
  logo?: File;
  background?: File;
}

export type MenuItemList = {
  categoryId: string;
  categoryName: string;
  items: {
    name: string;
    id: string;
    profilePicture: string;
  }[];
}[];

export enum FoodItemTemperature {
  HOT = "HOT",
  COLD = "COLD",
  WARM = "WARM",
}

export enum PriceBy {
  WEIGHT = "WEIGHT",
  EACH = "EACH",
}

export enum DriaryAttribute {
  VET = "VEGETARIAN",
  VEGAN = "VEGAN",
  GLUTEN_FREE = "GLUTEN_FREE",
}

export interface FoodItem {
  id: string;
  name: string;
  profileImageUrl: string;
  categoryId: string[];
  categories: { id: string; name: string }[];
  price: number;
  deliveryPrice: number;
  priceBy: string;
  vat: number;
  temperature: string; // temparrture config id
  dietaryAttributes: string[];
  outOfStock: boolean;
  isActive: boolean;
  description: string;
  modifierGroups: FoodCustomisation[];
}

export interface FoodItemDTO {
  id: string;
  name: string;
  description?: string | null;
  ingredients: string | null;
  dietaryAttributes: string | null;
  outOfStock: boolean;
  isActive?: boolean;
  outOfStockUntilTo: Date | null;
  profileImage?: string | null;
  price: string;
  deliveryPrice: string;
  createdAt?: string;
  updatedAt?: string;
  recipe?: string | null;
  temperature?: string;
  categories?: Array<{
    id: string;
    name: string;
    createdAt: string;
    updatedAt: string;
  }>;
  menus?: Array<{
    id: string;
    name: string;
  }>;
  itemModifiers?: Array<{
    id: string;
    modifierGroupId: string;
    orderIndex: number;
    createdAt: string;
    updatedAt: string;
    modifierGroup?: {
      id: string;
      name: string;
      description: string | null;
      minSelection: number | null;
      maxSelection: number | null;
      isRequired: boolean;
      multipleSelections: boolean;
      maxQuantityOfEachModifier: number | null;
      note: string | null;
      createdAt: string;
      updatedAt: string;
    };
  }>;
}

export type CreateMenuItemParam = Omit<FoodItem, "id" | "profileImageUrl"> & {
  profileImage?: File;
  categoryIds: string[];
};

export type MenuItemConfigParam = {
  foodItemTemperatures: { id: string; name: string }[];
  priceByOptions: { id: string; name: string }[];
  dietaryAttributes: { id: string; name: string }[];
};

export type Modifier = {
  id?: string;
  name: string;
  description?: string;
  price: number;
  maxQuantity?: number | null;
  orderIndex?: number;
  createdAt?: Date;
  updatedAt?: Date;
};

export type FoodCustomisation = {
  id: string;
  name: string;
  note: string;
  multipleSelections?: boolean;
  isRequired?: boolean;
  maxQuantityPerOption?: number;
  minSelection?: number;
  maxSelection?: number;
  modifiers?: Modifier[];
};

export type CreateCustomisation = {
  name: string;
  note: string;
};

export type CreateModifier = {
  name: string;
  price: number;
  description: string;
};

export enum RestaurantStatuses {
  OPEN = "OPEN",
  BUSY = "BUSY",
  PAUSED = "PAUSED",
  CLOSE = "CLOSE",
}

export type MerchantCategory = {
  // cuisine
  slug: string;
  name: string;
};

export type RestaurantStatus =
  | {
      type: RestaurantStatuses.OPEN;
      remainingTime: null;
      addingTime: null;
      pauseUntilNextOpenTime: null;
    }
  | {
      type: RestaurantStatuses.CLOSE;
      remainingTime: null | number;
      addingTime: null;
      pauseUntilNextOpenTime: null;
    }
  | {
      type: RestaurantStatuses.BUSY;
      addingTime: number;
      remainingTime: number;
      pauseUntilNextOpenTime: null;
    } // adding tiem in minutes
  | {
      type: RestaurantStatuses.PAUSED;
      remainingTime: number;
      addingTime: null;
      pauseUntilNextOpenTime: boolean;
    };

export type PromotionType =
  | "DISCOUNT_PRICE"
  | "FREE_ITEM"
  | "POINT_PRICE_OFF"
  | "POINT_FREE_ITEM"
  | "NEW_CUSTOMER_DISCOUNT";

export interface CreatePromotionDTO {
  name: string;
  description: string;
  type: PromotionType;
  minimumSpend?: number;
  minimumToEarn?: number;
  discountAmount?: number;
  freeItemId?: string;
  freeItemQuantity?: number;
  requiredPoints?: number;
  startDate: Date;
  endDate: Date;
  maxUsagePerUser: number;
  visible: boolean;
  availableDays: number[];
}

export interface UpdatePromotionDTO extends Partial<CreatePromotionDTO> {
  isActive?: boolean;
}

export interface Promotion {
  id: string;
  name: string;
  description: string;
  type: PromotionType;
  minimumSpend: number | null;
  minimumToEarn: number | null;
  discountAmount: number | null;
  discountPercentage?: number;
  freeItemId: string | null;
  freeItem: {
    id: string;
    name: string;
  } | null;
  freeItemQuantity: number | null;
  requiredPoints: number | null;
  startDate: Date;
  endDate: Date;
  validityPeriodHours: number;
  isActive: boolean;
  code: string;
  restaurantId: string;
  createdAt: Date;
  updatedAt: Date;
  maxUsagePerUser: number;
  isVisible: boolean;
  availableDays: number[];
}

export interface PaginatedPromotions {
  data: Promotion[];
  total: number;
  page: number;
}

export interface PromotionPerformanceData {
  newCustomerCount: number;
  totalSales: number;
  totalAmountSpent: number;
  totalOrders: number;
  dailySales: { date: Date; totalSales: number }[];
}

export abstract class FoodService {
  abstract getRestaurantProfile(): Promise<RestaurantProfile>;

  abstract saveRestaurantProfile(
    updateData: UpdateRestauranProfileParam,
    profilePicture?: File,
    bannerPicture?: File,
  ): Promise<void>;

  abstract becomeFoodMerchant(
    restaurantProfle: UpdateRestauranProfileParam,
  ): Promise<void>;

  abstract createNewMenu(createDto: CreateMenuParam): Promise<MenuProfile>;
  abstract updateMenu(createDto: MenuProfile): Promise<MenuProfile>;

  abstract getMenuDetail(id: string): Observable<MenuProfile>;

  abstract getMenuList(): Observable<Array<MenuShallowProfile>>;
  abstract getCategoryList(): Observable<Array<CategoryWithMenu>>;

  abstract getItemDetail(id: string): Observable<FoodItem | null>;
  abstract getItemModifierGroups(
    id: string,
  ): Observable<FoodItem["modifierGroups"]>;
  abstract deleteItemModifierGroup(id: string, modId: string): Observable<any>;
  abstract updateMenuItem(
    menuItem: CreateMenuItemParam & { id: string },
  ): Observable<FoodItem>;
  abstract deleteMenuItem(id: string): Observable<any>;
  abstract createMenuItem(menuItem: CreateMenuItemParam): Observable<FoodItem>;
  abstract checkIfFoodMerchant(): Observable<boolean>;
  abstract getCategoryDetail(id: string): Observable<CategoryWithMenu>;
  abstract getConfiguration(): Observable<MenuItemConfigParam>;

  abstract getFoodItems(params: {
    page: number;
    search?: string;
    limit: number;
    categoryIds?: string[];
  }): Observable<PaginationDto<FoodItemDTO>>;

  abstract addNewCategory(createDto: CreateCategoryParam): Observable<Category>;
  abstract updateCategory(category: Partial<Category>): Observable<Category>;

  abstract getCustomisations(params: {
    page: number;
    search: string;
    limit: number;
  }): Promise<PaginationDto<FoodCustomisation>>;

  abstract getItemsOnCustomisation(params: {
    modifierGroupId: string;
    page: number;
    limit: number;
    search: string;
  }): Observable<PaginationDto<OrderItem & { isAssigned: boolean }>>;

  abstract createCustomisation(
    createCustomisation: CreateCustomisation,
  ): Promise<{
    data: FoodCustomisation;
  }>;

  abstract updateCustomisation(
    updatedCustomisation: CreateCustomisation & { id: string },
  ): Promise<{
    data: FoodCustomisation;
  }>;

  abstract deleteCustomisation(modifierGroupId: string): Promise<{
    success: boolean;
  }>;

  abstract addModifierToModifierGroup(
    modifierGroupId: string,
    createModifier: CreateModifier,
  ): Promise<Modifier>;

  abstract deleteModifier(
    modifierGroupId: string,
    modifierId: string,
  ): Promise<{
    success: boolean;
  }>;

  abstract updateModifierDisplayOrderIndex(
    modifierGroupId: string,
    modifiers: { id: string; orderIndex: number }[],
  ): Promise<FoodCustomisation>;

  abstract updateModifierGroupRules(
    modifierGroupId: string,
    rules: {
      minSelection: number;
      maxSelection: number;
      multipleSelections: boolean;
      maxQuantityOfEachModifier?: number;
      isRequired: boolean;
    },
  ): Promise<FoodCustomisation>;

  abstract updateItemsInCustomisation(
    modifierGroupId: string,
    addedItemIds: string[],
    removedItemIds: string[],
  ): Promise<FoodCustomisation>;

  abstract updateItemCategoryOrder(
    categoryId: string,
    ids: string[],
  ): Promise<void>;
  abstract updateCategoryOrder(menuId: string, catIds: string[]): Promise<void>;

  abstract updateCategoryDisplayType(
    menuId: string,
    catId: string,
    displayType: MenuCategoryDisplayType,
  ): Promise<void>;

  abstract updateRestaurantStatus(
    status: RestaurantStatus,
  ): Observable<RestaurantStatus>;

  abstract getRestaurantStatus(): Observable<RestaurantStatus>;

  abstract getConstants(): Observable<Constants>;

  abstract getAllMerchantCategory(): Observable<MerchantCategory[]>;

  abstract getPromotions(
    page: number,
    limit: number,
    filter: {
      types?: PromotionType[];
      isActive?: boolean;
    },
  ): Observable<PaginatedPromotions>;

  abstract createPromotion(
    promotionData: CreatePromotionDTO,
  ): Observable<Promotion>;

  abstract updatePromotion(
    promotionId: string,
    updateData: UpdatePromotionDTO,
  ): Observable<Promotion>;
  abstract deletePromotion(promotionId: string): Observable<void>;
  abstract getPromotionById(promotionId: string): Observable<Promotion>;
  abstract getPromotionPerformanceById(
    promotionId: string,
    query: { startDate?: Date; endDate?: Date },
  ): Observable<PromotionPerformanceData>;
  abstract updatePromotionStatus(
    promotionId: string,
    isActive: boolean,
  ): Observable<void>;

  abstract updateAddress(address: RestaurantAddress): Observable<void>;

  abstract addAddress(address: Omit<RestaurantAddress, "id">): Observable<void>;

  abstract removeAddress(addressId: string): Observable<void>;

  abstract getAddresses(): Observable<RestaurantAddress[]>;

  abstract selectAddressAsDefault(id: string): Observable<void>;

  abstract getNotifUnreadCount(): Observable<number>;

  abstract getRestaurants(): Observable<Restaurant[]>;

  abstract saveRestaurantOrderSetting(
    orderSetting: OrderConfigSetting,
  ): Observable<void>;

  abstract bulkEditMenuItems(
    items: {
      id: string;
      name?: string;
      price?: string;
    }[],
  ): Observable<any>;
}
